/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Group, Space, rem, useMantineTheme } from '@mantine/core';
import { PaymentElement } from '@stripe/react-stripe-js';
import { isNil } from '@toss/utils';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { usePaymentCards } from '@fai/friendli-suite/features/settings/team-settings/use-payment';
import { ampTrack } from '@fai/friendli-suite/shared/amplitude';

import { ErrorMessageBox, SetupIntentFormButtons } from './components';
import { useCheckStripeSetupIntentCompletion, useStripeClientSecret } from './hooks';

/**
 * @url https://docs.stripe.com/testing#cards
 */
export default function SetupIntentForm({ onCompleted }: { onCompleted?: VoidFunction }) {
  const theme = useMantineTheme();
  const { teamId = '' } = useParams();

  const { stripeClientSecret } = useStripeClientSecret();
  const { cards } = usePaymentCards(teamId);

  useEffect(() => {
    const isFirstCreditCard = cards.length === 0;

    ampTrack.retention.ATTEMPT_ADD_CREDIT_CARD({ first: isFirstCreditCard });
  }, [cards.length]);

  const { onCheckStripeSetupIntentCompletion, error, loading } =
    useCheckStripeSetupIntentCompletion();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onCheckStripeSetupIntentCompletion(stripeClientSecret);

    if (onCompleted) onCompleted();
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      <Space h={theme.pf.layout.xs} />

      {error && <ErrorMessageBox />}
      <Space h={rem(32)} />

      <Group justify="flex-end" py={rem(4)}>
        <SetupIntentFormButtons disabled={isNil(stripeClientSecret)} isLoading={loading} />
      </Group>
    </form>
  );
}
